import React, { useState } from "react";
import { Card, Upload, Select } from "antd";
import { PDFDocument } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist";
import "pdfjs-dist/build/pdf.worker.entry";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import StandardButton from "../components/StandardButton";

const PdfCompressor = () => {
  const [pdfFile, setPdfFile] = useState(null);
  const [compressedPdfUrl, setCompressedPdfUrl] = useState(null);
  const [compressionLevel, setCompressionLevel] = useState("medium");
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const beforeUpload = (file) => {
    if (file.type !== "application/pdf") {
      toast.error(t("only_pdf_supported"));
      return false;
    }
    setPdfFile(file);
    return false;
  };

  const compressPdf = async () => {
    if (!pdfFile) return;
    setLoading(true);

    const toastId = toast.loading(`${t("processing")} 0%`);

    try {
      const arrayBuffer = await pdfFile.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const totalPages = pdf.numPages;

      const newPdfDoc = await PDFDocument.create();

      let scale;
      if (compressionLevel === "high") scale = 1.5;
      else if (compressionLevel === "medium") scale = 1.0;
      else if (compressionLevel === "low") scale = 0.6;

      for (let i = 1; i <= totalPages; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale });

        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({ canvasContext: context, viewport }).promise;

        const dataUrl = canvas.toDataURL("image/jpeg", 0.7);

        const jpgImageBytes = await fetch(dataUrl).then((res) => res.arrayBuffer());
        const embeddedImage = await newPdfDoc.embedJpg(jpgImageBytes);

        const pageWidth = embeddedImage.width;
        const pageHeight = embeddedImage.height;

        const newPage = newPdfDoc.addPage([pageWidth, pageHeight]);
        newPage.drawImage(embeddedImage, {
          x: 0,
          y: 0,
          width: pageWidth,
          height: pageHeight,
        });

        const progress = Math.round((i / totalPages) * 100);
        toast.loading(`${t("processing")} ${progress}%`, { id: toastId });
      }

      const compressedPdfBytes = await newPdfDoc.save();
      const compressedBlob = new Blob([compressedPdfBytes], { type: "application/pdf" });
      const compressedUrl = URL.createObjectURL(compressedBlob);
      setCompressedPdfUrl(compressedUrl);

      toast.success(t("completed_successfully"), { id: toastId });
    } catch (err) {
      toast.error(t("something_went_wrong"), { id: toastId });
    }

    setLoading(false);
  };

  const downloadCompressedPdf = () => {
    if (!compressedPdfUrl) return;

    const link = document.createElement("a");
    link.href = compressedPdfUrl;
    link.download = `compressed.pdf`;
    link.click();
  };

  return (
    <div className="min-h-screen bg-gray-50 p-0">
      <Header />
      <div className="max-w-5xl mx-auto">
        <Card className="p-6 rounded-xl shadow-lg space-y-6">
          {!pdfFile && (
            <div className="min-h-[70vh] flex items-center justify-center">
              <Upload beforeUpload={beforeUpload} showUploadList={false} accept=".pdf">
                <StandardButton size="large" type="secondary">
                  {t("upload_pdf_simple")}
                </StandardButton>
              </Upload>
            </div>
          )}

          {pdfFile && (
            <>
              <div className="flex flex-col md:flex-row md:items-center justify-center gap-4 text-center">
                <Select
                  className="w-full md:w-1/4"
                  value={compressionLevel}
                  onChange={(value) => setCompressionLevel(value)}
                >
                  <Select.Option value="high">{t("compression_high_quality")}</Select.Option>
                  <Select.Option value="medium">{t("compression_medium_quality")}</Select.Option>
                  <Select.Option value="low">{t("compression_low_quality")}</Select.Option>
                </Select>

                <StandardButton onClick={compressPdf} disabled={loading} className="w-full md:w-auto" type="secondary" loading={loading}>
                  {loading ? t("processing") : t("compress_button")}
                </StandardButton>

                {compressedPdfUrl && (
                  <StandardButton onClick={downloadCompressedPdf} type="secondary" className="w-full md:w-auto">
                    {t("download_button")}
                  </StandardButton>
                )}
              </div>

              <div className="w-full mt-8">
                <iframe
                  src={compressedPdfUrl ? compressedPdfUrl : URL.createObjectURL(pdfFile)}
                  title="PDF Preview"
                  className="w-full h-[800px] rounded-lg border shadow"
                />
              </div>
            </>
          )}
        </Card>
        <Footer />
      </div>
    </div>
  );
};

export default PdfCompressor;